import { useMemo, useState } from 'react';
import type { DataISO, Evento, GiornoCalendario } from '../api/tipi.ts';
import {
  type GiornoMese,
  griglieMese,
  intestazioniSettimana,
  nomeMese,
} from './calendario.ts';
import stili from './MiniCalendario.module.css';

/**
 * Calendario mensile compatto con le proprie assenze.
 *
 * Risponde alla domanda per cui la maggior parte delle persone apre
 * l'applicazione — "me le hanno approvate?" — senza far leggere niente: il
 * verde e il giallo si riconoscono a colpo d'occhio, e sono gli stessi colori
 * che il personale gia' associa a quegli stati nel file Excel.
 */

export interface ProprietaMiniCalendario {
  eventi: readonly Evento[];
  giorni: readonly GiornoCalendario[];
  meseIniziale?: { anno: number; mese: number };
  onGiorno?: (giorno: GiornoMese) => void;
}

export function MiniCalendario({
  eventi,
  giorni,
  meseIniziale,
  onGiorno,
}: ProprietaMiniCalendario) {
  const oggi = dataDiOggi();
  const [mostrato, setMostrato] = useState(
    meseIniziale ?? { anno: Number(oggi.slice(0, 4)), mese: Number(oggi.slice(5, 7)) },
  );

  const perGiorno = useMemo(() => {
    const mappa = new Map<DataISO, Evento>();
    for (const evento of eventi) {
      // Un giorno rifiutato torna libero: senza saltarlo qui il clic sulla
      // cella resterebbe bloccato, e la persona non potrebbe piu' chiedere
      // quel giorno nemmeno dopo aver letto il motivo del rifiuto.
      if (evento.stato === 'ANNULLATO' || evento.stato === 'RIFIUTATO') continue;
      for (
        let t = Date.parse(`${evento.dataInizio}T00:00:00Z`);
        t <= Date.parse(`${evento.dataFine}T00:00:00Z`);
        t += 86_400_000
      ) {
        mappa.set(new Date(t).toISOString().slice(0, 10), evento);
      }
    }
    return mappa;
  }, [eventi]);

  const info = useMemo(() => new Map(giorni.map((g) => [g.data, g])), [giorni]);

  const settimane = useMemo(
    () => griglieMese(mostrato.anno, mostrato.mese, perGiorno, info),
    [mostrato, perGiorno, info],
  );

  const cambiaMese = (delta: number) => {
    const indice = mostrato.anno * 12 + (mostrato.mese - 1) + delta;
    setMostrato({ anno: Math.floor(indice / 12), mese: (indice % 12) + 1 });
  };

  return (
    <div className={stili.riquadro}>
      <div className={stili.testata}>
        <button
          type="button"
          className={stili.freccia}
          onClick={() => cambiaMese(-1)}
          aria-label="Mese precedente"
        >
          ‹
        </button>
        <span className={stili.mese}>{nomeMese(mostrato.anno, mostrato.mese)}</span>
        <button
          type="button"
          className={stili.freccia}
          onClick={() => cambiaMese(1)}
          aria-label="Mese successivo"
        >
          ›
        </button>
      </div>

      <div className={stili.griglia} role="grid">
        {intestazioniSettimana().map((lettera, i) => (
          <span key={i} className={stili.intestazione} aria-hidden="true">
            {lettera}
          </span>
        ))}

        {settimane.flat().map((giorno) => {
          const etichetta = descrizione(giorno);
          return (
            <button
              key={giorno.data}
              type="button"
              className={[
                stili.giorno,
                giorno.nelMese ? '' : stili.fuoriMese,
                giorno.festivita ? stili.festivo : giorno.weekend ? stili.weekend : '',
                giorno.stato === 'approvato' ? stili.approvato : '',
                giorno.stato === 'in-attesa' ? stili.inAttesa : '',
                giorno.data === oggi ? stili.oggi : '',
              ]
                .filter(Boolean)
                .join(' ')}
              title={etichetta}
              aria-label={etichetta}
              onClick={() => onGiorno?.(giorno)}
            >
              {giorno.giorno}
            </button>
          );
        })}
      </div>

      <div className={stili.legenda}>
        <span>
          <i className={`${stili.pallino} ${stili.approvato}`} /> approvato
        </span>
        <span>
          <i className={`${stili.pallino} ${stili.inAttesa}`} /> in attesa
        </span>
        <span>
          <i className={`${stili.pallino} ${stili.festivo}`} /> festivo
        </span>
      </div>
    </div>
  );
}

function descrizione(giorno: GiornoMese): string {
  const parti: string[] = [giorno.data.split('-').reverse().join('/')];
  if (giorno.festivita) parti.push(giorno.festivita);
  if (giorno.evento) {
    parti.push(giorno.evento.etichetta);
    parti.push(giorno.stato === 'approvato' ? 'approvato' : 'in attesa di approvazione');
  }
  return parti.join(' — ');
}

function dataDiOggi(): DataISO {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
}
